import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchWorkOrders, fetchWorkOrder, createWorkOrder, updateWorkOrderStatus, completePickup,
  fetchIntakeLines, fetchEstimates, fetchWorkOrderAssets,
} from '@/services/workOrderService';
import type { WorkOrder } from '@/types/models';

export function useWorkOrders() {
  return useQuery({
    queryKey: ['work-orders'],
    queryFn: fetchWorkOrders,
    staleTime: 30_000,
  });
}

export function useWorkOrder(id: string | undefined) {
  return useQuery({
    queryKey: ['work-orders', id],
    queryFn: () => fetchWorkOrder(id!),
    enabled: !!id,
  });
}

export function useCreateWorkOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createWorkOrder,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['work-orders'] }),
  });
}

export function useUpdateWorkOrderStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: WorkOrder['status'] }) =>
      updateWorkOrderStatus(id, status),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['work-orders'] });
      qc.invalidateQueries({ queryKey: ['work-orders', id] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useCompletePickup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Parameters<typeof completePickup>[1] }) =>
      completePickup(id, payload),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['work-orders'] });
      qc.invalidateQueries({ queryKey: ['work-orders', id] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useIntakeLines(workOrderId: string | undefined) {
  return useQuery({
    queryKey: ['intake-lines', workOrderId],
    queryFn: () => fetchIntakeLines(workOrderId!),
    enabled: !!workOrderId,
  });
}

export function useEstimates(workOrderId: string | undefined) {
  return useQuery({
    queryKey: ['estimates', workOrderId],
    queryFn: () => fetchEstimates(workOrderId!),
    enabled: !!workOrderId,
  });
}

export function useWorkOrderAssets(workOrderId: string | undefined) {
  return useQuery({
    queryKey: ['assets', 'work-order', workOrderId],
    queryFn: () => fetchWorkOrderAssets(workOrderId!),
    enabled: !!workOrderId,
  });
}
